// React
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Components
import {
  Field,
  Select,
  SelectFilter,
  Label,
  DisplayError,
} from "../project-create/CreateProject.components";
import { CreateProjectMap } from "../project-create/CreateProjectMap";
import Modal from "../modal/Modal";

// Functions
import { tranStr, translate } from "../../utils/translation";
import {
  sectorList,
  subsectorList,
  qualityList,
  typeList,
  currencyList,
  unitOfMeasureList,
} from "../project-create/CreateProject.functions";
import {
  RegionSelect,
  CountrySelect,
  CitySelect,
} from "../locations/LocationSelect";
import { useLocations } from "../../api/admin/RegionManagementAPI";

// API
import { saveBenchmarkCollection } from "../../api/benchmarking/BenchmarkCollectionsAPI";

// Style

import "./BenchmarkCollectionsSave.css";
import { hasRoles } from "../../utils/roles";
import isEmpty from "../../validation/is-empty";

BenchmarkCollectionsSave.propTypes = {
  user: PropTypes.object,
  selectedCollection: PropTypes.object,
  benchmarkCollections: PropTypes.object,
  isCreate: PropTypes.bool,
  setSelectedCollection: PropTypes.func,
};

export default function BenchmarkCollectionsSave(props) {
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { user } = props;
  const { isCreate } = props;
  const { selectedCollection, setSelectedCollection } = props;
  const { benchmarkCollections } = props;

  if (hasRoles(user.roles, ["Client"])) {
    return null;
  }

  return (
    <div>
      <button
        className="button general-upload-button"
        onClick={() => {
          setIsEditOpen(!isEditOpen);
        }}
      >
        {isCreate ? "Create" : "Edit"}
      </button>
      <Modal
        title={translate("Edit Analysis")}
        Component={BenchmarkingCollectionsEditPage}
        modal={isEditOpen}
        setModal={setIsEditOpen}
        // Props
        benchmarkCollections={benchmarkCollections}
        selectedCollection={selectedCollection}
        setSelectedCollection={setSelectedCollection}
        user={user}
      />
    </div>
  );
}

BenchmarkingCollectionsEditPage.propTypes = {
  user: PropTypes.object,
  setModal: PropTypes.func,
  benchmarkCollections: PropTypes.object,
  selectedCollection: PropTypes.object,
  setSelectedCollection: PropTypes.func,
};

function BenchmarkingCollectionsEditPage(props) {
  const { user } = props;
  const { benchmarkCollections } = props;
  const { selectedCollection } = props;
  const { setModal } = props;
  const { setSelectedCollection } = props;

  // These 2 are important because they are required when updating the existing record
  const [benchmarkId, setBenchmarkId] = useState(null); // will get created if null
  const [companyId, setCompanyId] = useState(null); // will get created if null

  const [projectName, setProjectName] = useState("");
  const [sector, setSector] = useState("");
  const [subsector, setSubsector] = useState("");
  const [quality, setQuality] = useState("");
  const [projectType, setProjectType] = useState("");
  const [currency, setCurrency] = useState("");
  const [unitOfMeasure, setUnitOfMeasure] = useState("");
  const [escalationYear, setEscalationYear] = useState("");

  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [lat, setLat] = useState("55");
  const [lng, setLng] = useState("55");

  const [localRegionFactor, setLocalRegionFactor] = useState(false);
  const [currencyFactor, setCurrencyFactor] = useState(false);

  const [benchmarkComparisons, setBenchmarkComparisons] = useState([]);

  // Add if this is edit
  useEffect(() => {
    if (!isEmpty(selectedCollection)) {
      // These 2 are important because they are required when updating the existing record
      setBenchmarkId(selectedCollection.id);
      setCompanyId(selectedCollection.companyId);

      setProjectName(selectedCollection.project_name);
      setSector(selectedCollection.sector);
      setSubsector(selectedCollection.sub_sector);
      setQuality(selectedCollection.quality);
      setProjectType(selectedCollection.project_type);
      setCurrency(selectedCollection.currency);
      setUnitOfMeasure(selectedCollection.unitOfMeasure);

      let parsedDate = new Date(selectedCollection.base_date);
      let year = parsedDate.toLocaleString("default", { year: "numeric" });
      setEscalationYear(year);

      setRegion(selectedCollection.region);
      setCountry(selectedCollection.country);
      setCity(selectedCollection.city);
      setLat(selectedCollection.lat);
      setLng(selectedCollection.lng);

      setBenchmarkComparisons(selectedCollection.linkedBenchmarksIds);
    }
  }, [selectedCollection]);

  // API
  const { locations } = useLocations(true);

  // Errors
  const [errors, setErrors] = useState({});

  return (
    <div className="benchmark-create-configuration-modal">
      <div className="benchmark-create-configuration-modal-content-container">
        <div className="benchmark-create-configuration-modal-content boxed">
          <h1 className="display-4 form-title">
            <i className="far fa-file-alt"></i>
            <span>Report Details</span>
          </h1>
          <Field
            label={translate("Collection Name")}
            placeholder={tranStr("Collection 1")}
            value={projectName}
            onChange={setProjectName}
            error={errors.ProjectName}
          />
          <Select
            label={translate("Sector")}
            placeholder={tranStr("Sector")}
            value={sector}
            options={sectorList()}
            onChange={setSector}
            error={errors.Sector}
          />
          <SelectFilter
            label={translate("Subsector")}
            placeholder={tranStr("Subsector")}
            value={subsector}
            options={subsectorList()}
            onChange={setSubsector}
            error={errors.SubSector}
            filter={sector}
          />
          <Select
            label={translate("Project Quality")}
            placeholder={tranStr("Quality")}
            value={quality}
            options={qualityList()}
            onChange={setQuality}
            error={errors.Quality}
          />
          <Select
            label={translate("Project Type")}
            placeholder={tranStr("Type")}
            value={projectType}
            options={typeList()}
            onChange={setProjectType}
            error={errors.ProjectType}
          />
          <Select
            label={translate("Currency")}
            placeholder={tranStr("Currency")}
            value={currency}
            options={currencyList()}
            onChange={setCurrency}
            error={errors.Currency}
          />
          <Select
            label={translate("Displayed Unit")}
            placeholder={tranStr("Displayed Unit")}
            value={unitOfMeasure}
            options={unitOfMeasureList()}
            onChange={setUnitOfMeasure}
            error={errors.UnitOfMeasure}
          />
        </div>
        <div className="benchmark-create-configuration-modal-content boxed">
          <h1 className="display-4 form-title">
            <i className="fas fa-sliders-h"></i>
            <span>Parameters</span>
          </h1>
          <div className="create-project-input-row">
            <Label label={translate("Region")} />
            <RegionSelect
              value={region}
              set={setRegion}
              options={locations.data}
              isLoading={locations.loading}
              classType={"create-project-location-select"}
              setCountry={setCountry}
              setCity={setCity}
            />
            <DisplayError error={errors.Region} />
          </div>
          <div className="create-project-input-row">
            <Label label={translate("Country")} />
            <CountrySelect
              value={country}
              set={setCountry}
              options={locations.data}
              classType={"create-project-location-select"}
              region={region}
              setCity={setCity}
            />
            <DisplayError error={errors.Country} />
          </div>
          <div className="create-project-input-row">
            <Label label={translate("City")} />
            <CitySelect
              value={city}
              set={setCity}
              options={locations.data}
              classType={"create-project-location-select"}
              country={country}
            />
            <DisplayError error={errors.City} />
          </div>
          <Field
            label={translate("Escalation Year")}
            placeholder={tranStr("YYYY")}
            value={escalationYear}
            onChange={setEscalationYear}
            error={errors.EscalationYear}
          />
          <div className="benchmark-map">
            <div className="coordinate-inputs">
              <Label label={"Latitude"} />
              <input
                className="create-project-input"
                placeholder={lat}
                value={lat}
                onChange={(e) => {
                  // Only permit numbers and "-" and "." characters
                  if (
                    !isNaN(e.target.value) ||
                    e.target.value === "-" ||
                    e.target.value === "."
                  ) {
                    setLat(e.target.value);
                  }
                }}
              ></input>
              <DisplayError error={errors.Lat} />
              <Label label={"Longitude"} />
              <input
                className="create-project-input"
                placeholder={lng}
                value={lng}
                onChange={(e) => {
                  // Only permit numbers and "-" and "." characters
                  if (
                    !isNaN(e.target.value) ||
                    e.target.value === "-" ||
                    e.target.value === "."
                  ) {
                    setLng(e.target.value);
                  }
                }}
              ></input>
              <DisplayError error={errors.Lat} />
            </div>
            <CreateProjectMap lat={lat} lng={lng} />
          </div>
        </div>
        <div className="benchmark-create-configuration-modal-content boxed">
          <h1 className="display-4 form-title">
            <i className="fas fa-ruler"></i>
            <span>Metrics</span>
          </h1>
          <div className="text">
            <p>The following metrics are selected as default:</p>
            <ul className="text-left">
              <li>Total Number of Floors</li>
              <li>Local Region Area</li>
              <li>Total Construction Cost</li>
              <li>Total Project Cost</li>
              <li>Construction Cost per GIA</li>
              <li>Total Building Services Cost per GIA</li>
            </ul>
          </div>
        </div>
        <div className="benchmark-create-configuration-modal-content headless">
          <div className="toggle">
            Global Benchmarking{""}
            <i
              className="fas fa-question-circle icon"
              title="Global Benchmarking enables comparison across different regions. It uses Global Location Factors and converts currencies as defined in the parameters."
            ></i>
            {""}
            <label className="switch">
              <input
                type="checkbox"
                checked={currencyFactor}
                onChange={(e) => {
                  setCurrencyFactor(!currencyFactor);
                }}
              />
              {""}
              ""
              <span className="slider round"></span>
            </label>
          </div>
        </div>
        <div className="benchmark-create-configuration-modal-content headless">
          <div className="toggle">
            Local Region Area{""}
            <i
              className="fas fa-question-circle icon"
              title="Local Region Area changes the analysis from a cost/GIA basis to the project's cost/local region area (ie cost/MW, cost/bed, cost/room)"
            ></i>
            <label className="switch">
              <input
                type="checkbox"
                checked={localRegionFactor}
                onChange={(e) => {
                  setLocalRegionFactor(!localRegionFactor);
                }}
              />
              {""}
              ""
              <span className="slider round"></span>
            </label>
          </div>
        </div>
      </div>
      <div className="general-row-container-double">
        <div className="feedback-success">
          <b>{errors.saving}</b>
        </div>
      </div>
      <button
        className="modal-save-button"
        onClick={() => {
          const payload = {
            // IDs
            BenchmarkId: benchmarkId, // will get created if null
            CompanyId: companyId, // will get created if null

            // Benchmark Details
            ProjectName: projectName,
            ProjectNameShort: projectName,
            Client: "Created from Benchmark Collection",
            FileName: "Created from Benchmark Collection",
            ContactEmail: "Created from Benchmark Collection",
            AnalysisBasis: "Created from Benchmark Collection",
            Source: "Created from Benchmark Collection",
            ProcurementMethod: "Created from Benchmark Collection",
            ExclusiveProjectId: null,
            Standard: "Created from Benchmark Collection",

            // Benchmark Dates
            BaseDate: new Date(escalationYear || Date.now()).toISOString(),
            CompiledDate: new Date(Date.now()).toISOString(), // Keep the dates the same

            // Benchmark Details
            Quality: quality,
            Sector: sector,
            SubSector: subsector,
            ProjectType: projectType,
            Currency: currency,
            CurrencySymbol: "",
            Region: region,
            Country: country,
            City: city,
            UnitOfMeasure: unitOfMeasure,

            Lat: lat,
            Lng: lng,
            CurrencyFactor: currencyFactor,
            LocalRegionFactor: localRegionFactor,

            TpiCity: city,

            // Benchmark Measurement
            MeasurementSystem: "Created from Benchmark Collection",
            LocalRegionArea: 0,
            LocalRegionAreaDescription: "Created from Benchmark Collection",

            // Sensitivity
            InformationSensitivityLevel: 3,

            // Benchmark Costs
            ConstructionCost: 0,

            // Form State - Rows
            BenchmarkElements: [],
            // Form State - Metrics
            BenchmarkMetrics: [],
            // Benchmark Comparisons
            BenchmarkComparisonsIds: benchmarkComparisons,
          };

          saveBenchmarkCollection(
            user,
            payload,
            setErrors,
            setModal,
            benchmarkCollections,
            selectedCollection,
            setSelectedCollection
          );
        }}
      >
        Save
      </button>
    </div>
  );
}
